import { getOktaConfigForEnvironment } from "./oktaEnvironments";
import { getSensitiveItem, setSensitiveItem } from "../services/storageService";
import logger from "../../logger";

export const loginWithOfficeDialog = (environment) => {
  return new Promise((resolve, reject) => {
    if (!environment) {
      reject(new Error("Environment must be specified"));
      return;
    }

    const envConfig = getOktaConfigForEnvironment(environment);
    if (!Office || !Office.context || !Office.context.ui || !Office.context.ui.displayDialogAsync) {
      reject(
        new Error(
          "Office.js Dialog API is not available. Make sure you're running in an Office Add-in environment."
        )
      );
      return;
    }

    let baseUrl = window.location.origin;

    if (baseUrl.includes("localhost:3000") && baseUrl.startsWith("http://")) {
      baseUrl = baseUrl.replace("http://", "https://");
    }

    const loginUrlString = baseUrl + "/login.html";
    const loginUrl = new URL(loginUrlString);
    const dialogRedirectUri = baseUrl + "/login.html";

    logger.debug("Auth dialog config prepared for environment");
    const configParam = {
      issuer: envConfig.issuer,
      clientId: envConfig.clientId,
      redirectUri: dialogRedirectUri,
      scopes: envConfig.scopes,
    };
    loginUrl.searchParams.set("config", encodeURIComponent(JSON.stringify(configParam)));

    try {
      localStorage.setItem("okta-dialog-config", JSON.stringify(configParam));
    } catch (e) {
      logger.warn("Could not store dialog config:", e);
    }

    const finalUrl = loginUrl.href;
    logger.debug("Opening Office dialog");

    Office.context.ui.displayDialogAsync(
      finalUrl,
      {
        height: 60,
        width: 50,
      },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          logger.error("Error opening dialog:", result.error);
          reject(
            new Error(
              "Failed to open dialog: " +
                (result.error?.message || result.error?.name || "Unknown error")
            )
          );
          return;
        }

        const dialog = result.value;
        logger.info("Dialog opened successfully");

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg) => {
          try {
            let message;
            try {
              message = JSON.parse(arg.message);
            } catch {
              if (arg.message === "success") {
                logger.info("Received 'success' message from dialog - closing dialog");
                dialog._loginSuccessful = true;
                dialog.close();
                return;
              }
              throw new Error("Failed to parse message: " + arg.message);
            }

            logger.debug("Message received from dialog, type: %s", message.type);

            if (message.type === "OKTA_AUTH_SUCCESS") {
              logger.info("[DIAG:authDialog] OKTA_AUTH_SUCCESS received");

              dialog._loginSuccessful = true;

              if (message.tokens) {
                try {
                  const hasAccessToken = !!message.tokens.accessToken;
                  const hasIdToken = !!message.tokens.idToken;
                  const hasRefreshToken = !!message.tokens.refreshToken;
                  const accessTokenType = hasAccessToken
                    ? typeof message.tokens.accessToken
                    : "missing";
                  const accessTokenShape =
                    hasAccessToken && typeof message.tokens.accessToken === "object"
                      ? Object.keys(message.tokens.accessToken).join(",")
                      : accessTokenType;
                  const expiresAtRoot = message.tokens.expiresAt || null;
                  const expiresAtAccess = message.tokens.accessToken?.expiresAt || null;
                  const expiresAtId = message.tokens.idToken?.expiresAt || null;

                  logger.info(
                    "[DIAG:authDialog] Token shape from dialog: hasAccessToken=%s, hasIdToken=%s, hasRefreshToken=%s, accessTokenType=%s, accessTokenShape=[%s], expiresAt(root)=%s, expiresAt(access)=%s, expiresAt(id)=%s",
                    hasAccessToken,
                    hasIdToken,
                    hasRefreshToken,
                    accessTokenType,
                    accessTokenShape,
                    expiresAtRoot,
                    expiresAtAccess,
                    expiresAtId
                  );

                  if (!message.tokens.accessToken && !message.tokens.idToken) {
                    throw new Error("Invalid tokens received: missing accessToken and idToken");
                  }

                  const runtimeAvailable =
                    typeof OfficeRuntime !== "undefined" && !!OfficeRuntime.storage;
                  logger.info(
                    "[DIAG:authDialog] OfficeRuntime.storage available in taskpane: %s (typeof OfficeRuntime=%s)",
                    runtimeAvailable,
                    typeof OfficeRuntime
                  );

                  if (runtimeAvailable) {
                    const tokensJson = JSON.stringify(message.tokens);
                    logger.info(
                      "[DIAG:authDialog] Writing to OfficeRuntime.storage key='authTokens', payload size=%d bytes",
                      tokensJson.length
                    );
                    await OfficeRuntime.storage.setItem("authTokens", tokensJson);

                    const verifyRead = await OfficeRuntime.storage.getItem("authTokens");
                    const verifyOk = !!verifyRead && verifyRead.length === tokensJson.length;
                    logger.info(
                      "[DIAG:authDialog] Verify read-back from OfficeRuntime.storage: success=%s, readSize=%s, expectedSize=%d",
                      verifyOk,
                      verifyRead ? verifyRead.length : "null",
                      tokensJson.length
                    );

                    if (!verifyOk) {
                      logger.error(
                        "[DIAG:authDialog] VERIFICATION FAILED: OfficeRuntime.storage write did not persist correctly"
                      );
                      throw new Error(
                        "Token storage verification failed for OfficeRuntime.storage. Your session was not saved securely."
                      );
                    }
                  } else {
                    const tokensJson = JSON.stringify(message.tokens);
                    await setSensitiveItem("authTokens", tokensJson);
                    const fallbackRead = await getSensitiveItem("authTokens");
                    const fallbackOk = !!fallbackRead && fallbackRead.length === tokensJson.length;
                    if (!fallbackOk) {
                      throw new Error(
                        "Token storage verification failed. Your session was not saved securely."
                      );
                    }
                    logger.debug(
                      "[DIAG:authDialog] OfficeRuntime.storage NOT available; tokens stored via storage service"
                    );
                  }
                } catch (storageError) {
                  logger.error(
                    "[DIAG:authDialog] Error storing tokens: %s (name=%s)",
                    storageError.message,
                    storageError.name
                  );
                  const tokensJson = JSON.stringify(message.tokens);
                  await setSensitiveItem("authTokens", tokensJson);
                  const verifyRead = await getSensitiveItem("authTokens");
                  const verifyOk = !!verifyRead && verifyRead.length === tokensJson.length;
                  if (!verifyOk) {
                    throw new Error(
                      "Failed to store tokens after retry: " +
                        (storageError?.message || "verification failed")
                    );
                  }
                  logger.debug("Tokens stored via storage service (fallback)");
                }
              } else {
                const errorMsg = "No tokens received in message";
                logger.error("[DIAG:authDialog] %s", errorMsg);
                throw new Error(errorMsg);
              }

              resolve({
                user: message.user,
                tokens: message.tokens,
              });
              logger.info("[DIAG:authDialog] Auth dialog promise resolved successfully");
            } else if (message.type === "OKTA_AUTH_ERROR") {
              logger.error("Authentication error from dialog:", message.error);

              dialog.close();
              reject(new Error(message.error || "Authentication failed"));
            } else {
              logger.warn("Unknown message type from dialog:", message.type);
            }
          } catch (error) {
            logger.error("Error parsing message from dialog: %s", error.message);
            dialog.close();
            reject(new Error("Failed to parse message from dialog: " + error.message));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          logger.debug("Dialog event received, error code: %d", arg.error);
          if (arg.error === 12006) {
            logger.debug("Dialog closed (12006)");
            if (!dialog._loginSuccessful) {
              logger.debug("Dialog closed before login completed");
              reject(new Error("Login dialog was closed"));
            } else {
              logger.debug("Dialog closed after successful login");
            }
          } else {
            logger.debug("Other dialog event");
          }
        });
      }
    );
  });
};

export const isOfficeDialogAvailable = () => {
  return !!(
    typeof Office !== "undefined" &&
    Office.context &&
    Office.context.ui &&
    Office.context.ui.displayDialogAsync
  );
};
